import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMobileLayout from 'Hooks/useMobileLayout';
import { MobileStepper } from '@material-ui/core';
import classNames from 'classnames';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import CircularProgress from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/CircularProgress';
import SendIcon from '@material-ui/icons/Send';
import RoundedButton from 'Components/ComponentLibrary/Components/Uncategorized/RoundedButton';

const styles = theme => ({
  root: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
    display: 'flex',
    justifyContent: 'center',
    justifyItems: 'center',
  },
  desktopActions: {
    display: 'flex',
    gap: '32px',
  },
});

function StepActions({
  classes,
  onChange,
  disabledNext,
  disabledBack,
  activeStep,
  totalSteps,
  isFinalStep,
  nextButtonText,
  backButtonText,
  editMode,
  saveButtonText,
  loading,
}) {
  const isMobile = useMobileLayout();
  const { logEvent } = useEventTrackingLogEvent();

  const handleNext = () => {
    logEvent('Account Application: Next button clicked');
    onChange(activeStep + 1);
  };

  const handleBack = () => {
    logEvent('Account Application: Back button clicked');
    onChange(activeStep - 1);
  };

  const handleSave = () => {
    logEvent('Account Information Edit: Save button clicked');
    onChange(activeStep);
  };

  const getIcon = () => {
    if (!loading && isFinalStep) {
      return <SendIcon color="white" fontSize="16px" />;
    }
    if (isFinalStep) {
      return (<CircularProgress
        size={16}
        color="primary"
      />);
    }
    return null;
  };

  const getView = () => {
    if (editMode) {
      return (
        <div className={classes.desktopActions}>
          <RoundedButton
            test="apex-form-back-button"
            disableElevation
            disabled={disabledNext}
            onClick={handleSave}
            className={classes.button}
            variant="contained"
            color="primary"
            size={'lg'}
          >
            {saveButtonText}
          </RoundedButton>
        </div>
      );
    }

    return isMobile ? (
      <MobileStepper
        activeStep={activeStep || 0}
        steps={totalSteps}
        nextButton={
          <RoundedButton
            test="apex-form-next-button"
            disableElevation
            variant="contained"
            color="primary"
            onClick={handleNext}
            disabled={disabledNext}
            endIcon={getIcon()}
          >
            {nextButtonText}
          </RoundedButton>
                }
        backButton={
          <RoundedButton
            test="apex-form-back-button"
            disableElevation
            disabled={activeStep === 0}
            onClick={handleBack}
            className={classes.button}
          >
            {backButtonText}
          </RoundedButton>
                }
      />
    ) : (
      <div className={classes.desktopActions}>
        <RoundedButton
          test="apex-form-back-button"
          disableElevation
          disabled={activeStep === 0 || disabledBack}
          onClick={handleBack}
          className={classes.button}
          size={'lg'}
        >
          {backButtonText}
        </RoundedButton>
        <RoundedButton
          test="apex-form-next-button"
          disableElevation
          variant="contained"
          color="primary"
          onClick={handleNext}
          disabled={disabledNext}
          endIcon={getIcon()}
          size={'lg'}
        >
          {nextButtonText}
        </RoundedButton>
      </div>
    );
  };

  return (
    <div
      className={classNames(classes.root, {
        [classes.desktopActions]: !isMobile,
        [classes.desktopActions]: isMobile && editMode,
      })}
    >
      {getView()}
    </div>
  );
}

StepActions.defaultProps = {
  nextButtonText: 'Next',
  backButtonText: 'Back',
  activeStep: null,
  totalSteps: 0,
  isFinalStep: false,
  disabledBack: false,
  disabledNext: false,
  editMode: false,
  loading: false,
  saveButtonText: 'Save',
};

StepActions.propTypes = {
  totalSteps: PropTypes.number,
  activeStep: PropTypes.number,
  isFinalStep: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  disabledBack: PropTypes.bool,
  disabledNext: PropTypes.bool,
  nextButtonText: PropTypes.string,
  backButtonText: PropTypes.string,
  editMode: PropTypes.bool,
  loading: PropTypes.bool,
  saveButtonText: PropTypes.string,
};

export default withStyles(styles)(StepActions);
